import { useEffect } from 'react';
import { LEGAL_DOCUMENTS, type LegalDocument } from '@anon-chat/shared';

/**
 * Правила и политика конфиденциальности.
 *
 * Отдельные адреса, а не окно поверх лендинга: на документы ссылается бот и их
 * присылают в поддержке, а ссылку на всплывающее окно передать нельзя.
 *
 * Из поисковой выдачи они исключены: нужны тем, кто пришёл по ссылке, а в
 * выдаче только оттягивали бы показы у страниц самого сервиса.
 */

/** Какой документ показывать по текущему адресу. */
export function documentForPath(pathname: string): LegalDocument | null {
  const slug = pathname.replace(/^\/+|\/+$/g, '');
  return LEGAL_DOCUMENTS.find((document) => document.slug === slug) ?? null;
}

export function LegalScreen({ document: legal }: { document: LegalDocument }): React.ReactNode {
  /*
    Из поисковой выдачи документы исключены. Запрет ставит и Nginx заголовком,
    но страница может отдаваться и не им — например, при локальной сборке или
    через стороннюю раздачу статики.
  */
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.append(meta);
    document.title = `${legal.title} — ghostly.im`;
    return () => meta.remove();
  }, [legal.title]);

  return (
    <div className="h-full w-full overflow-y-auto">
      <article className="mx-auto flex w-full max-w-2xl flex-col px-5 py-10">
        <a
          href="/"
          className="mb-6 self-start text-[14px] text-accent underline decoration-dotted underline-offset-4"
        >
          ← На главную
        </a>

        <h1 className="text-[26px] font-semibold leading-tight text-text">{legal.title}</h1>
        <p className="mt-1 text-[13px] text-text-muted">
          Последнее изменение: {legal.updatedAt}
        </p>
        <p className="mt-4 text-[15px] leading-relaxed text-text-muted">{legal.intro}</p>

        {legal.sections.map((section) => (
          <section key={section.title} className="mt-7">
            <h2 className="text-[17px] font-medium text-text">{section.title}</h2>

            {section.paragraphs?.map((paragraph) => (
              <p key={paragraph} className="mt-2 text-[15px] leading-relaxed text-text-muted">
                {paragraph}
              </p>
            ))}

            {section.items ? (
              <ul className="mt-2 flex flex-col gap-1.5">
                {section.items.map((item) => (
                  <li
                    key={item}
                    className="flex gap-2 text-[15px] leading-relaxed text-text-muted"
                  >
                    <span aria-hidden className="mt-[9px] size-1 shrink-0 rounded-full bg-accent" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
        ))}

        <footer className="mt-10 border-t border-border pt-5 text-[13px] text-text-muted">
          {LEGAL_DOCUMENTS.filter((other) => other.slug !== legal.slug).map((other) => (
            <a
              key={other.slug}
              href={`/${other.slug}`}
              className="underline decoration-dotted underline-offset-4"
            >
              {other.title}
            </a>
          ))}
        </footer>
      </article>
    </div>
  );
}
